import { AssessmentContext } from 'app/ext/mcg-informed/assessment-context';
import * as Mcg from 'app/ext/mcg-informed/interfaces';

export type SanitizationResults = {
    annotatables: Mcg.IAnnotatable[],
    annotatableStrings: string[],
    annotatableText: string
}

export class McgInformedDataMethods {

    ////****************** Functions ************************************************////

    public static hideFollowupsRecursive(question: Mcg.Question | null, context: AssessmentContext): void {
        if(!question) return;

        question.questionReferencesSet.forEach(qr => {
            if(context.selectedQuestionReferenceIds.indexOf(`|${qr.id}`) > -1) {
                context.selectedQuestionReferenceIds = context.selectedQuestionReferenceIds.replace(`|${qr.id}`, '');
            }

            context.questionComponents.forEach(qc => {
                if(!qc.question) return;
                if(qc.question.id === qr.id && qc.clearSelectedAnswers) {
                    qc.clearSelectedAnswers();
                }
            });

            const followupQuestion: Mcg.Question | null | undefined = context.questionComponents.find(qc => qc.question?.id === qr.id)?.question;
            if(followupQuestion) this.hideFollowupsRecursive(followupQuestion, context);
        });
    }

    public static sanitizeText(annotatable: Mcg.IAnnotatable): string {
        let s: string = annotatable.text;
        let annotatables: Mcg.IAnnotatable[] = [];

        let hasAnnotations: boolean = (annotatable.annotationSet && annotatable.annotationSet.length > 0) || (annotatable.citationSet && annotatable.citationSet.length > 0) || (annotatable.footnoteSet && annotatable.footnoteSet.length > 0);
        const hasPdfLinks: boolean = s.indexOf('<ulink') > -1;

        let sanitizationResults: SanitizationResults | null = null;

        if(hasAnnotations) {
            sanitizationResults = this.sanitizeAnnotations(annotatable, s);
            s = sanitizationResults.annotatableText;
            annotatables = sanitizationResults.annotatables;
        }

        if(hasPdfLinks) s = this.sanitizePdfLinks(s);

        s = s.trim();
        s = s.replace(/\<emphasis\>/g, '').replace(/\<\/emphasis\>/g, '');

        return s;
    }

    public static sanitizeVersion(version: string): string {
        return version.indexOf('.') > -1 ? version.substring(0, version.indexOf('.')) : version;
    }

    public static sanitizeAnnotations(annotatable: Mcg.IAnnotatable, annotatableText: string): SanitizationResults {
        let annotatables: Mcg.IAnnotatable[] = [];
        const annotatableStrings: string[] = [];

        annotatableStrings.push(...this.getAnnotatables(annotatableText));

        annotatable.annotationSet.forEach(a => {
            const id: string = '[[Ann:' + a.id + ']]';
            const i: number = annotatableStrings.indexOf(id) + 1;
            annotatableText = annotatableText.replace(id, '');
            a.sequence = i;
            annotatables.push(a);
        });

        annotatable.citationSet.forEach(c => {
            const id: string = '[[Cit:' + c.id + ']]';
            const i: number = annotatableStrings.indexOf(id) + 1;
            annotatableText = annotatableText.replace(id, '');
            c.sequence = i;
            annotatables.push(c);
        });

        annotatable.footnoteSet.forEach(f => {
            const id: string = '[[Foot:' + f.id + ']]';
            const i: number = annotatableStrings.indexOf(id) + 1;
            annotatableText = annotatableText.replace(id, '');
            f.sequence = i;
            annotatables.push(f);
        });

        annotatables = this.sortAnnotatables(annotatables);

        const results: SanitizationResults = {
            annotatables: annotatables,
            annotatableStrings: annotatableStrings,
            annotatableText: annotatableText
        };

        return results;
    }

    private static getAnnotatables(text: string): string[] {
        const regex = /\[\[[A-Za-z]+:[A-Za-z0-9_]+\]\]/mg;
        const matches = text.match(regex);
        const results: string[] = [];

        matches?.forEach(m => {
            results.push(m);
        });

        return results;
    }

    private static sortAnnotatables(annotatables: Mcg.IAnnotatable[]): Mcg.IAnnotatable[] {
        annotatables = annotatables.sort(function(a, b) {
            return a.sequence! < b.sequence! ? -1 : 1;
        });

        return annotatables;
    }

    private static sanitizePdfLinks(annotatableText: string): string {
        return annotatableText.substring(annotatableText.indexOf(">") + 1, annotatableText.indexOf("</ulink>"));
    }

}
